import type { InitCache, ServerSetting } from "../types/session.ts";

/**
 * Reads a value from the ServerSettings array in InitCache. Each entry has
 * shape { Key, Value, Type, LocalTS, AdminTS, Local } (matches the backend
 * IDS.Classes.Admin.ServerSetting DataContract). Key match is case-insensitive
 * to mirror WebAdmin2's ServerSettingsDic behavior (stored keys can vary in case).
 */
export function getServerSetting(
  cache: Pick<InitCache, "ServerSettings"> | null | undefined,
  key: string,
): string | undefined {
  if (!cache?.ServerSettings) return undefined;
  const needle = key.toUpperCase();
  for (const entry of cache.ServerSettings as ServerSetting[]) {
    if (entry.Key && entry.Key.toUpperCase() === needle) {
      const v = entry.Value;
      if (v === null || v === undefined) return undefined;
      return String(v);
    }
  }
  return undefined;
}

/**
 * Builds the primary URL for a product image: `{ImageURL}/{folder}/{operatorId?}/{filename}`.
 * Returns `""` when the base URL or filename is missing — callers should
 * render the `<img>` only when the URL is non-empty and pair it with
 * {@link buildProductImageUrlFallback} via `onError` so the base `/f/` folder
 * is tried when the operator sub-folder has no match.
 *
 * Folder: `"f"` = full-size, `"t"` = thumbnail.
 */
let _warnedMissingImageUrl = false;

export function buildProductImageUrl(
  cache: Pick<InitCache, "ServerSettings"> | null | undefined,
  filename: string | null | undefined,
  operatorId?: number | null,
  folder: "f" | "t" = "f",
): string {
  if (!filename) return "";
  const base = getServerSetting(cache, "ImageURL")?.replace(/\/+$/, "");
  if (!base) {
    if (!_warnedMissingImageUrl && cache?.ServerSettings) {
      _warnedMissingImageUrl = true;
      const keys = (cache.ServerSettings as ServerSetting[])
        .map(s => s.Key)
        .filter(Boolean);
      // eslint-disable-next-line no-console
      console.warn(
        "[images] ServerSettings 'ImageURL' missing — product images will not render. " +
        "Ensure the server exposes an 'ImageURL' setting for this endpoint. " +
        `Available keys: ${keys.join(", ") || "(none)"}`,
      );
    }
    return "";
  }
  const opSeg = operatorId && operatorId > 0 ? `${operatorId}/` : "";
  // No encodeURIComponent — SafeProductImageFilename on the server already
  // stripped anything requiring escaping; matches WebAdmin2's behavior so the
  // URL shape is identical across both admins.
  return `${base}/${folder}/${opSeg}${filename}`;
}

/**
 * Fallback URL without the operator sub-folder — `{ImageURL}/{folder}/{filename}`.
 * Paired with {@link buildProductImageUrl} in an `<img onError>` handler for
 * products whose image was saved under the base `/f/` (no operator) folder.
 */
export function buildProductImageUrlFallback(
  cache: Pick<InitCache, "ServerSettings"> | null | undefined,
  filename: string | null | undefined,
  folder: "f" | "t" = "f",
): string {
  if (!filename) return "";
  const base = getServerSetting(cache, "ImageURL")?.replace(/\/+$/, "");
  if (!base) return "";
  return `${base}/${folder}/${filename}`;
}

/**
 * Display URLs to try, in order, for a small preview of a product image: the thumbnail in the
 * operator folder, the full-size file there (additional-image uploads write only `/f/`), then the
 * full-size file in the base folder. Empty and duplicate URLs are dropped, so a base-folder image
 * (operator 0) yields two candidates and a missing `ImageURL` setting yields none.
 */
export function buildProductImageThumbCandidates(
  cache: Pick<InitCache, "ServerSettings"> | null | undefined,
  filename: string | null | undefined,
  operatorId?: number | null,
): string[] {
  const urls = [
    buildProductImageUrl(cache, filename, operatorId, "t"),
    buildProductImageUrl(cache, filename, operatorId, "f"),
    buildProductImageUrlFallback(cache, filename, "f"),
  ];
  return urls.filter((u, i) => !!u && urls.indexOf(u) === i);
}
